/** TODO temporary. will be refactored */

import {
    Component,
    EventEmitter,
    Input,
    NgZone,
    OnInit,
    Optional,
    Output,
} from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { IOption } from '@cmps/ng-select';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, filter, tap } from 'rxjs/operators';

import { SecurityScope } from '../auth/auth.model';
import { AuthService } from '../auth/auth.service';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../common/take-until-destroy.decorator';
import { StateToProps } from '../store';
import { useDispatcher, useStore } from '../store.decorator';

import { Column } from './experimental.grid';
import {
    gridSort,
    rootChangeCriteria,
    rootClearState,
    rootInit,
    rootListLookups,
    rootReset,
    rootSearch,
    rootSearchCancel,
    rootSelectUser,
    rootToggleFavorite,
} from './search-user.action';
import { SearchUserConfigService } from './search-user.config.service';
import { SearchUserDialogRef } from './search-user.dialog-ref';
import { CriteriaFormValue, ParameterFormValue } from './search-user.form';
import {
    ParameterOpts,
    ParameterType,
    StatusOpts,
    UserSearchResult,
} from './search-user.model';
import searchUserReducer from './search-user.reducer';
import { RootState } from './search-user.state';
import { User } from './user.model';

function parseToOptions<T extends { id: number; name: string }>(
    items: T[],
): IOption[] {
    return (items || []).map((s) => {
        return { label: s.name, value: s.id };
    });
}

function createParameterFormGroup(
    formBuilder: FormBuilder,
    param: ParameterFormValue,
): FormGroup {
    const group = formBuilder.group({
        parameterType: [param.parameterType],
        value: [param.value],
        status: [param.status],
    });
    group
        .get('parameterType')
        .valueChanges.pipe(tap(() => group.get('value').setValue('')))
        .subscribe();
    return group;
}

function toggleParameterTypes(
    parameterOpts: IOption[],
    criteria: CriteriaFormValue,
): void {
    const lookup = criteria.parameters.map((e) => e.parameterType);
    // eslint-disable-next-line no-return-assign, @typescript-eslint/prefer-includes
    parameterOpts.forEach((e) => (e.disabled = lookup.indexOf(e.value) !== -1));
}

function patchForm(
    formBuilder: FormBuilder,
    form: FormGroup,
    parameterOpts: IOption[],
    criteria: CriteriaFormValue,
    blackoutHdl: { _blackout: boolean },
): void {
    blackoutHdl._blackout = true;
    // eslint-disable-next-line no-return-assign
    parameterOpts.forEach((e) => (e.disabled = false));

    const parameters = form.get('parameters') as FormArray;
    const num = parameters.controls.length;
    for (let i = 0; i < num; i++) {
        parameters.removeAt(0);
    }
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    if (criteria && criteria.parameters && criteria.parameters.length) {
        criteria.parameters.forEach((e) => {
            parameters.push(createParameterFormGroup(formBuilder, e));
        });

        toggleParameterTypes(parameterOpts, criteria);
    }
    blackoutHdl._blackout = false;

    form.patchValue(criteria || {}, { emitEvent: false });
}

@Component({
    selector: '[searchuser]',
    templateUrl: './search-user.container.component.html',
})
@TakeUntilDestroy()
export class SearchUserContainerComponent
    implements OnInit, TakeUntilDestroy, StateToProps<RootState>
{
    state: RootState;
    /** @input */ storePathPrefix: string;

    /** @private */ _SecurityScope = SecurityScope;

    @Input() userId: number;
    @Input() scope: SecurityScope;
    @Input() theme: 'current' | 'next';

    /** when true, emit 'select' event when the user is changed, instead of only when _onSelectClick is invoked */
    @Input() nonExplicitEmit: boolean;

    @Output() select: EventEmitter<User>;
    /** @private */ _statusOpts: IOption[];
    /** @private */ _parameterOpts: IOption[];
    /** @private */ _form: FormGroup;
    /** @private */ _parameterLookupOpts: { [key: string]: IOption[] }; // ParameterType
    /** @private */ _criteria: CriteriaFormValue;
    /** @private */ _blackout: boolean;

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _formBuilder: FormBuilder,
        private readonly _translateService: TranslateService,
        private readonly _authService: AuthService,
        private readonly _router: Router,
        private readonly _configService: SearchUserConfigService,
        @Optional() private readonly _userSearchDialogRef: SearchUserDialogRef,
    ) {
        this.theme = 'current';
        this.select = new EventEmitter();

        this._statusOpts = StatusOpts.map((e) => ({
            label: this._translateService.instant(e.tlKey),
            value: e.value,
        }));

        this._parameterOpts = ParameterOpts.map((e) => ({
            label: this._translateService.instant(e.tlKey),
            value: e.value,
        }));
    }

    ngOnInit(): void {
        const path = 'searchUser';
        useStore(
            this,
            !this.storePathPrefix ? path : `${this.storePathPrefix}.${path}`,
            searchUserReducer.searchUser,
        );

        useDispatcher(
            this,
            rootInit({
                selectedUserId:
                    this.userId != null
                        ? this.userId
                        : this._authService.user.id,
                scope: this.scope,
                authUserId: this._authService.user.id,
            }),
        );

        useDispatcher(this, rootListLookups({}));

        this._form = this._formBuilder.group({
            statusType: [null],
            parameters: this._formBuilder.array([]),
        });

        this._form.valueChanges
            .pipe(
                filter(() => !this._blackout),
                debounceTime(100),
                tap((value) => {
                    // do this here rather that making a subscription/event-binding for each parameter
                    toggleParameterTypes(this._parameterOpts, value);
                    this._parameterOpts = [...this._parameterOpts];

                    useDispatcher(
                        this,
                        rootChangeCriteria({ criteria: value }),
                    );
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        patchForm(
            this._formBuilder,
            this._form,
            this._parameterOpts,
            this.state.criteria,
            this,
        );

        const action = rootClearState({});
        action.storePath = (this as any)._storePath;
        this._configService.logoutActions.push(action);
    }

    ngOnDestroy(): void {
        // yes, only cancel search
        useDispatcher(this, rootSearchCancel({}));
        this.select.complete();
    }

    stateToProps(prevState?: RootState): void {
        if (!this._form) {
            return;
        }

        if (
            prevState.lobs !== this.state.lobs ||
            prevState.regions !== this.state.regions ||
            prevState.areas !== this.state.areas ||
            prevState.sites !== this.state.sites ||
            prevState.superGroups !== this.state.superGroups ||
            prevState.roles !== this.state.roles
        ) {
            this._parameterLookupOpts = {
                [ParameterType.LOB_ID]: parseToOptions(this.state.lobs),
                [ParameterType.REGION_ID]: parseToOptions(this.state.regions),
                [ParameterType.AREA_ID]: parseToOptions(this.state.areas),
                [ParameterType.SITE_ID]: parseToOptions(this.state.sites),
                [ParameterType.SUPER_GROUP_ID]: parseToOptions(
                    this.state.superGroups,
                ),
                [ParameterType.ROLE_ID]: parseToOptions(this.state.roles),
            };
        }

        if (
            prevState.criteria !== this.state.criteria &&
            prevState.formNeedsUpdate !== this.state.formNeedsUpdate
        ) {
            patchForm(
                this._formBuilder,
                this._form,
                this._parameterOpts,
                this.state.criteria,
                this,
            );
        }

        if (
            this.nonExplicitEmit &&
            prevState.selectedUserProfile !== this.state.selectedUserProfile
        ) {
            this._onSelectClick();
        }
    }

    propsToDispatch(): void {
        // TODO revise. this is temporary means of flushing tabs after auth user has changed
        if (
            this.state.authUserId != null &&
            this.state.authUserId !== this._authService.user.id
        ) {
            useDispatcher(this, rootReset({ hard: true }));
        }
    }

    /** @private */ _onAddParameterClick(): void {
        const parameters = this._form.get('parameters') as FormArray;
        const typeIndex = this._parameterOpts.findIndex((e) => !e.disabled);
        if (typeIndex === -1) {
            return;
        }
        parameters.push(
            createParameterFormGroup(this._formBuilder, {
                parameterType: this._parameterOpts[typeIndex].value,
                status: this._form.get('statusType').value,
            }),
        );
        toggleParameterTypes(this._parameterOpts, this._form.value);
    }

    /** @private */ _onRemoveParameterClick(index: number): void {
        const parameters = this._form.get('parameters') as FormArray;
        const obs = parameters.at(index).get('parameterType')
            .valueChanges as Subject<any>;
        // eslint-disable-next-line @typescript-eslint/dot-notation
        if (!obs['complete']) {
            console.warn('subscription leak');
        } else {
            obs.complete();
        }
        parameters.removeAt(index);
        toggleParameterTypes(this._parameterOpts, this._form.value);
    }

    /** @private */ _onSearchClick(): void {
        useDispatcher(this, rootSearch({}));
    }

    /** @private */ _onUserClick($event: number): void {
        useDispatcher(this, rootSelectUser({ userId: $event }));
    }

    /** @private */ _onToggleFavorite($event: UserSearchResult): void {
        useDispatcher(
            this,
            rootToggleFavorite({
                user: $event,
                refId: $event.userId.toString(),
            }),
        );
    }

    /** @private */ _onResetClick(): void {
        useDispatcher(this, rootReset({}));
    }

    /** @private */ _onSearchGridSortChange($event: Column): void {
        useDispatcher(this, gridSort({ column: $event }));
    }

    /** @private */ _onSelfClick(): void {
        // really want to use rootSelectUser. but immediate feedback is wanted
        this.select.emit(this._authService.user);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._userSearchDialogRef &&
            this._userSearchDialogRef.close(this._authService.user);
    }

    /** @private */ _onSelectClick(): void {
        this.select.emit(this.state.selectedUserProfile);
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._userSearchDialogRef &&
            this._userSearchDialogRef.close(this.state.selectedUserProfile);
    }
}
